import { useApp } from '@tldraw/editor'
import * as React from 'react'
import { IntlProvider, useIntl } from 'react-intl'
import { track } from 'signia-react'
import {
	DEFAULT_TRANSLATION,
	getTranslation,
	TLTranslation,
	TLTranslationKey,
} from './translations'

/** @public */
export interface TranslationProviderProps {
	children: any
	/** (optional) A collection of overrides different locales.
	 * @example
	 * ```ts
	 * <TranslationProvider overrides={{ en: { "style-panel.styles": "Properties" }}}/>
	 * ```
	 */
	overrides?: Record<string, Record<string, string>>
}

/**
 * Provides a translation context to the app.
 *
 * @public
 */
export const TranslationProvider = track(function TranslationProvider({
	overrides,
	children,
}: TranslationProviderProps) {
	const app = useApp()
	const locale = app.userSettings.locale

	const [currentTranslation, setCurrentTranslation] = React.useState<TLTranslation>(() => {
		if (overrides && overrides['en']) {
			return {
				...DEFAULT_TRANSLATION,
				messages: { ...DEFAULT_TRANSLATION.messages, ...overrides['en'] },
			}
		}

		return DEFAULT_TRANSLATION
	})

	React.useEffect(() => {
		let isCancelled = false

		async function loadTranslation() {
			const localeString = locale ?? navigator.language.split(/[-_]/)[0]
			const translation = await getTranslation(localeString)

			if (translation && !isCancelled) {
				if (overrides && overrides[localeString]) {
					setCurrentTranslation({
						...translation,
						messages: { ...translation.messages, ...overrides[localeString] },
					})
				} else {
					setCurrentTranslation(translation)
				}
			}
		}

		loadTranslation()

		return () => {
			isCancelled = true
		}
	}, [locale, overrides])

	return (
		<IntlProvider locale={currentTranslation.locale} messages={currentTranslation.messages}>
			{children}
		</IntlProvider>
	)
})

/**
 * Returns a function to translate a translation key into a string based on the current translation.
 *
 * @example
 * ```ts
 * 	const msg = useTranslation()
 * 	const label = msg("style-panel.styles")
 * ```
 *
 * @public
 */
export function useTranslation() {
	const intl = useIntl()
	return React.useCallback(
		function translateMessage(id: TLTranslationKey) {
			return intl.formatMessage({ id })
		},
		[intl]
	)
}
